import React, { useCallback, useMemo } from "react";
import { SilverSunnStripeSubscription } from "@switcherstudio/switcher-api-client";
import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import { SubscriptionWithPrice } from ".";
import {
    calculateDiscountedAmount,
    displayPlanName,
    isStudioPlanId
} from "helpers/stripe";
import { getLocalizedDisplayString } from "helpers/localization";
import { Widget } from "components/widgets/Widget";
import { Button } from "components/buttons/Button";
import { usePauseStatus } from "hooks/usePauseStatus";
import { displayDate } from "helpers/time";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { exists } from "helpers/booleans";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";

interface SubscriptionGridProps {
    subscriptions: SubscriptionWithPrice[] | undefined;
    isPastDue?: boolean;
    displayCostFn: (
        amount?: number,
        showPeriodText?: boolean
    ) => string | undefined;
    openManagePlanModal: () => void;
}

export const SubscriptionGrid: React.FC<SubscriptionGridProps> = ({
    subscriptions,
    isPastDue,
    displayCostFn,
    openManagePlanModal
}) => {
    const { t, i18n } = useTranslation();
    const {
        userIsPaused,
        userWillPause,
        pausedPlanName,
        pausedPlanId,
        pauseBeginDate
    } = usePauseStatus();

    const { userInfo } = useSelector((state: RootState) => state.user);

    const { data: pausedPlan } = useSwitcherClient(
        (client) => client.stripePrices_GetPriceInfo,
        {
            suppressErrorBoundary: true,
            requestImmediately: exists(pausedPlanId),
            args: [pausedPlanId]
        }
    );

    const displayDiscount = useCallback(
        (sub: SilverSunnStripeSubscription): string => {
            const amountOff = (sub.AmountOff || 0) / 100;

            if (sub.PercentOff !== null) {
                return ` (${t("misc:discount", {
                    amount: sub.PercentOff + "%"
                })})`;
            } else if (amountOff > 0) {
                return ` (${t("misc:discount", { amount: "$" + amountOff })})`;
            }

            return "";
        },
        [t]
    );

    const displayStatus = useMemo((): React.ReactElement => {
        if (isPastDue) {
            return (
                <strong className={styles["error"]}>
                    {t("subscription-page:past-due")}
                </strong>
            );
        }

        return <></>;
    }, [isPastDue, t]);

    const displayedAmount = useCallback(
        (subscription: SilverSunnStripeSubscription): number => {
            if (userIsPaused) {
                return pausedPlan?.Amount;
            } else {
                return calculateDiscountedAmount(
                    subscription?.Amount,
                    subscription
                );
            }
        },
        [pausedPlan, userIsPaused]
    );

    const displayedInterval = useMemo((): string => {
        if (userIsPaused) {
            if (pausedPlan?.IsRecurring) {
                return (
                    "/" +
                    (pausedPlan?.RecurringInterval === "month"
                        ? t("misc:month")
                        : t("misc:year"))
                );
            }
        } else {
            ("");
        }
    }, [pausedPlan, t, userIsPaused]);

    const subtext = useCallback(
        (subscription: SilverSunnStripeSubscription): React.ReactElement => {
            if (userWillPause) {
                return (
                    <p>
                        <span>{t("subscription-page:upcoming-pause")} </span>
                        {displayDate(
                            pauseBeginDate,
                            i18n.language,
                            "dddd, MMMM D, YYYY"
                        )}{" "}
                    </p>
                );
            } else {
                return (
                    <p>
                        {displayCostFn(displayedAmount(subscription), true) +
                            displayDiscount(subscription)}
                        {displayedInterval}
                        {displayStatus}
                    </p>
                );
            }
        },
        [
            t,
            userWillPause,
            pauseBeginDate,
            i18n.language,
            displayCostFn,
            displayedAmount,
            displayDiscount,
            displayedInterval,
            displayStatus
        ]
    );

    const getButtonText = useCallback(
        (subscription: SilverSunnStripeSubscription): "string" => {
            if (isPastDue) {
                return t("subscription-page:pay-now");
            }

            if (isStudioPlanId(subscription.PlanId)) {
                return t("subscription-page:upgrade-studio-plan");
            }

            return t("subscription-page:manage-plan");
        },
        [isPastDue, t]
    );

    const getButtonVariant = useCallback(
        (
            subscription: SilverSunnStripeSubscription
        ): "btn-primary" | "btn-outline-secondary" => {
            if (isPastDue) {
                return "btn-primary";
            }
            if (isStudioPlanId(subscription.PlanId)) {
                return "btn-primary";
            }

            if (userIsPaused) {
                return "btn-primary";
            }
            return "btn-outline-secondary";
        },
        [isPastDue, userIsPaused]
    );

    return (
        <>
            {subscriptions?.length === 0 ? (
                <Widget
                    title={t("subscription-page:plan-details").toUpperCase()}
                    details={[
                        {
                            title: t("subscription-page:no-active-plan")
                        }
                    ]}
                    button={
                        <Button type="primary" onClick={openManagePlanModal}>
                            {t("subscription-page:select-plan")}
                        </Button>
                    }
                    variant="billing"
                />
            ) : (
                <>
                    <div className={`${styles["plan-grid"]}`}>
                        {subscriptions?.map(({ subscription }, idx) => {
                            return (
                                <Widget
                                    key={idx}
                                    title={t(
                                        "subscription-page:plan-details"
                                    ).toUpperCase()}
                                    details={[
                                        {
                                            title: userIsPaused
                                                ? `${pausedPlanName} (${t(
                                                      "pause-copy:paused"
                                                  )})`
                                                : displayPlanName(
                                                      getLocalizedDisplayString(
                                                          userInfo?.Plan
                                                              ?.Details
                                                              ?.DisplayName,
                                                          i18n.language
                                                      ) ?? subscription.PlanName
                                                  ),
                                            children: subtext(subscription)
                                        }
                                    ]}
                                    button={
                                        <button
                                            className={`btn ${getButtonVariant(
                                                subscription
                                            )}`}
                                            onClick={openManagePlanModal}
                                        >
                                            {getButtonText(subscription)}
                                        </button>
                                    }
                                    variant="billing"
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </>
    );
};
